import { motion, AnimatePresence } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { Clock, Sun } from 'lucide-react'
import { ToggleSwitch, AnimatedChevron } from '@tendbot/ui'
import { formatTime12, formatRelativeDate } from '@tendbot/ui'
import { expandCollapse } from '@tendbot/ui'

const TIME_SLOTS: string[] = []
for (let h = 5; h <= 11; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hh = String(h).padStart(2, '0')
    const mm = String(m).padStart(2, '0')
    TIME_SLOTS.push(`${hh}:${mm}`)
  }
}

interface TimeCardProps {
  time: string
  weekdaysOnly: boolean
  nextRun: string
  isExpanded: boolean
  onToggleExpand: () => void
  onTimeChange: (time: string) => void
  onWeekdaysToggle: () => void
}

export default function TimeCard({
  time,
  weekdaysOnly,
  nextRun,
  isExpanded,
  onToggleExpand,
  onTimeChange,
  onWeekdaysToggle,
}: TimeCardProps) {
  const { t } = useTranslation('briefing')
  return (
    <div
      onClick={onToggleExpand}
      className="glass-card rounded-2xl overflow-hidden cursor-pointer"
    >
      <div className="p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gold/20 flex items-center justify-center">
            <Clock size={20} className="text-gold" />
          </div>
          <div>
            <p className="text-ivory font-medium">
              {formatTime12(time)} &middot; {weekdaysOnly ? t('schedule.weekdays') : t('schedule.everyDay')}
            </p>
            <p className="text-xs text-pearl/50 mt-0.5">
              {t('schedule.nextShort', { when: formatRelativeDate(nextRun), time: formatTime12(time) })}
            </p>
          </div>
        </div>
        <AnimatedChevron isOpen={isExpanded} size={16} />
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            {...expandCollapse}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-0 border-t border-white/5 space-y-4">
              {/* Time select */}
              <div className="pt-3 space-y-2">
                <label className="text-xs text-pearl/60 uppercase tracking-wider">{t('schedule.deliveryTime')}</label>
                <select
                  value={time}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onTimeChange(e.target.value)}
                  className="w-full bg-charcoal/50 border border-gold/20 rounded-lg px-4 py-3 text-ivory text-sm focus:outline-none focus:border-gold/40 appearance-none cursor-pointer"
                >
                  {TIME_SLOTS.map((slot: string) => (
                    <option key={slot} value={slot}>{formatTime12(slot)}</option>
                  ))}
                </select>
              </div>

              {/* Weekdays only toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ivory">{t('schedule.weekdaysOnly')}</p>
                  <p className="text-xs text-pearl/50">{t('schedule.weekdaysOnlyDesc')}</p>
                </div>
                <ToggleSwitch
                  checked={weekdaysOnly}
                  onChange={(e) => {
                    e?.stopPropagation()
                    onWeekdaysToggle()
                  }}
                  variant="bordered"
                />
              </div>

              {/* Next briefing preview */}
              <div className="bg-charcoal/30 rounded-lg px-4 py-3 flex items-center gap-2">
                <Sun size={14} className="text-gold/60" />
                <p className="text-xs text-pearl/60">
                  {t('schedule.nextBriefing', { when: formatRelativeDate(nextRun), time: formatTime12(time) })}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
